import { UniqueIdentifier } from "@dnd-kit/core";
import { useStore } from "@/stores/useStore";
import { useCardActions } from "@/hooks/useCardActions";
import { memo, useMemo } from "react";
import { Card, CardProps } from "@/components/Dashboard/components/Card/Card";
import { useRoles } from "@/hooks/useRoles";
import { Card as CardModel } from "@/types/v2";

export const SortableCard = ({
  cardId,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging,
  tentative,
  onMoveToClick,
}: Pick<
  CardProps,
  "isDragging" | "dragHandleListeners" | "dragHandleAttributes" | "tentative"
> & {
  cardId: string | UniqueIdentifier;
  onMoveToClick?: (card: CardModel) => void;
}) => {
  const { employees, shop, integration, tags } = useStore();
  const { isViewOnly } = useRoles();
  const {
    handleColorChange,
    handleTimerLimitChange,
    handleTimerStart,
    handleTimerPause,
    handleTimerReset,
    handleTimerStop,
    handleToggleStarred,
    card,
  } = useCardActions(cardId);

  // Memoize the drag handle props to prevent unnecessary re-renders
  const memoizedDragHandleListeners = useMemo<CardProps["dragHandleListeners"]>(
    () =>
      ({
        ...dragHandleListeners,
      }) as CardProps["dragHandleListeners"],
    [dragHandleListeners]
  );

  const memoizedDragHandleAttributes = useMemo<
    CardProps["dragHandleAttributes"]
  >(
    () =>
      ({
        ...dragHandleAttributes,
      }) as CardProps["dragHandleAttributes"],
    [dragHandleAttributes]
  );

  if (!shop || !card) {
    return null;
  }
  return (
    <Card
      cardData={card}
      integration={integration}
      onColorChange={handleColorChange}
      onTimerLimitChange={handleTimerLimitChange}
      onTimerStart={handleTimerStart}
      onTimerPause={handleTimerPause}
      onTimerReset={handleTimerReset}
      onTimerStop={handleTimerStop}
      onStarToggle={() => handleToggleStarred(!card.isStarred)}
      employees={employees}
      shop={shop}
      tags={tags}
      disabled={isViewOnly}
      isDragging={isDragging}
      tentative={tentative}
      dragHandleListeners={memoizedDragHandleListeners}
      dragHandleAttributes={memoizedDragHandleAttributes}
      onMoveToClick={onMoveToClick}
    />
  );
};

// Add comparison function for better memoization
export const MemoizedCard = memo(SortableCard, (prevProps, nextProps) => {
  // Custom comparison that treats identical listener objects as equal
  const listenersEqual =
    JSON.stringify(prevProps.dragHandleListeners) ===
    JSON.stringify(nextProps.dragHandleListeners);
  const attributesEqual =
    JSON.stringify(prevProps.dragHandleAttributes) ===
    JSON.stringify(nextProps.dragHandleAttributes);

  return (
    prevProps.cardId === nextProps.cardId &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.tentative === nextProps.tentative &&
    prevProps.onMoveToClick === nextProps.onMoveToClick &&
    listenersEqual &&
    attributesEqual
  );
});
